import { useState, useEffect } from 'react';
import { GOOGLE_MAPS_CONFIG, getApiKeyForEnvironment } from '../config/maps';

// Global Google Maps loading state
let googleMapsLoaded = false;
let googleMapsLoading = false;
let googleMapsError = null;
let googleMapsCallbacks = [];

// Load Google Maps script globally
const loadGoogleMapsScript = () => {
  return new Promise((resolve, reject) => {
    // If already loaded, resolve immediately
    if (googleMapsLoaded && window.google && window.google.maps) {
      resolve();
      return;
    }

    // If already loading, add to callbacks
    if (googleMapsLoading) {
      googleMapsCallbacks.push({ resolve, reject });
      return;
    }

    // If there was an error, reject immediately
    if (googleMapsError) {
      reject(googleMapsError);
      return;
    }

    // Start loading
    googleMapsLoading = true;
    const apiKey = getApiKeyForEnvironment();
    
    if (!apiKey) {
      const error = new Error('Google Maps API key not configured');
      googleMapsError = error;
      googleMapsLoading = false;
      reject(error);
      return;
    }

    // Create script element
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=${GOOGLE_MAPS_CONFIG.LIBRARIES.join(',')}&callback=initGoogleMaps`;
    script.async = true;
    script.defer = true;

    // Global callback function
    window.initGoogleMaps = () => {
      googleMapsLoaded = true;
      googleMapsLoading = false;
      
      // Resolve all pending callbacks
      googleMapsCallbacks.forEach(callback => callback.resolve());
      googleMapsCallbacks = [];
      
      resolve();
    };

    // Error handling
    script.onerror = (error) => {
      console.error('❌ Google Maps script failed to load:', error);
      console.error('❌ Script URL was:', script.src);
      googleMapsError = error;
      googleMapsLoading = false;
      
      // Reject all pending callbacks
      googleMapsCallbacks.forEach(callback => callback.reject(error));
      googleMapsCallbacks = [];
      
      reject(error);
    };

    // Add script to document
    document.head.appendChild(script);
  });
};

// Custom hook to use Google Maps
export const useGoogleMaps = () => {
  const [isLoaded, setIsLoaded] = useState(googleMapsLoaded);
  const [isLoading, setIsLoading] = useState(googleMapsLoading);
  const [error, setError] = useState(googleMapsError);

  useEffect(() => {
    if (googleMapsLoaded) {
      setIsLoaded(true);
      setIsLoading(false);
      setError(null);
      return;
    }

    if (googleMapsError) {
      setError(googleMapsError);
      setIsLoading(false);
      return;
    }

    if (googleMapsLoading) {
      setIsLoading(true);
      return;
    }

    // Load Google Maps
    setIsLoading(true);
    loadGoogleMapsScript()
      .then(() => {
        setIsLoaded(true);
        setIsLoading(false);
        setError(null);
      })
      .catch((err) => {
        setError(err);
        setIsLoading(false);
      });
  }, []);

  return {
    isLoaded,
    isLoading,
    error,
    loadGoogleMaps: loadGoogleMapsScript
  };
};

export default useGoogleMaps;
